import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'processButton',
    'criteriaField',
    'companyTemplateField',
    'lockingDateField',
    'mappingModeField',
    'overrideMappingsField',
    'initialMappingListField',
    'finalMappingListField',
  ]

  declare readonly processButtonTarget: HTMLButtonElement
  declare readonly hasProcessButtonTarget: boolean
  declare readonly criteriaFieldTargets: HTMLInputElement[]
  declare readonly companyTemplateFieldTarget: HTMLSelectElement
  declare readonly lockingDateFieldTarget: HTMLInputElement
  declare readonly mappingModeFieldTargets: HTMLInputElement[]
  declare readonly overrideMappingsFieldTarget: HTMLInputElement
  declare readonly hasOverrideMappingsFieldTarget: boolean
  declare readonly initialMappingListFieldTarget: HTMLSelectElement
  declare readonly finalMappingListFieldTarget: HTMLSelectElement

  private initialValues: Map<string, string> = new Map()
  private initialFileSelected = false
  private boundCheckForChanges!: () => void

  connect(): void {
    this.boundCheckForChanges = this.checkForChanges.bind(this)
    this.storeInitialValues()

    // Only set up change detection if process button exists
    if (this.hasProcessButtonTarget) {
      this.setupLockingDateListener()
      this.setupFormLevelListener()
    }
  }

  disconnect(): void {
    // Only clean up listeners if we set them up (i.e., if process button exists)
    if (this.hasProcessButtonTarget) {
      this.lockingDateFieldTarget.removeEventListener(
        'input',
        this.boundCheckForChanges
      )
      this.lockingDateFieldTarget.removeEventListener(
        'change',
        this.boundCheckForChanges
      )
      this.lockingDateFieldTarget.removeEventListener(
        'blur',
        this.boundCheckForChanges
      )
      this.element.removeEventListener(
        'change',
        this.boundCheckForChanges,
        true
      )
    }
  }

  setupLockingDateListener(): void {
    // Add direct event listeners to catch datepicker changes
    // These sometimes work better than Stimulus action syntax for third-party widgets
    this.lockingDateFieldTarget.addEventListener(
      'input',
      this.boundCheckForChanges
    )
    this.lockingDateFieldTarget.addEventListener(
      'change',
      this.boundCheckForChanges
    )
    this.lockingDateFieldTarget.addEventListener(
      'blur',
      this.boundCheckForChanges
    )
  }

  setupFormLevelListener(): void {
    // Listen to all form changes to catch jQuery datepicker changes indirectly
    // Using capture phase to catch events before they bubble
    this.element.addEventListener('change', this.boundCheckForChanges, true)
  }

  storeInitialValues(): void {
    // Store initial mapping list IDs
    this.initialValues.set(
      'initial_mapping_list_id',
      this.initialMappingListFieldTarget.value
    )
    this.initialValues.set(
      'final_mapping_list_id',
      this.finalMappingListFieldTarget.value
    )

    // Store initial criteria (radio button)
    const selectedCriteria = this.criteriaFieldTargets.find(
      (radio) => radio.checked
    )
    if (selectedCriteria) {
      this.initialValues.set('criteria', selectedCriteria.value)
    }

    // Store initial company_template_id
    this.initialValues.set(
      'company_template_id',
      this.companyTemplateFieldTarget.value
    )

    // Store initial locking_date
    this.initialValues.set('locking_date', this.lockingDateFieldTarget.value)

    // Store initial mapping_mode (radio button)
    const selectedMappingMode = this.mappingModeFieldTargets.find(
      (radio) => radio.checked
    )
    if (selectedMappingMode) {
      this.initialValues.set('mapping_mode', selectedMappingMode.value)
    }

    // Store initial file selection state
    if (this.hasOverrideMappingsFieldTarget) {
      this.initialFileSelected =
        this.overrideMappingsFieldTarget.files !== null &&
        this.overrideMappingsFieldTarget.files.length > 0
    }
  }

  checkForChanges(): void {
    // Only check for changes if process button exists
    if (!this.hasProcessButtonTarget) {
      return
    }

    let hasChanges = false

    // Check initial mapping list
    if (
      this.initialMappingListFieldTarget.value !==
      this.initialValues.get('initial_mapping_list_id')
    ) {
      hasChanges = true
    }

    // Check final mapping list
    if (
      this.finalMappingListFieldTarget.value !==
      this.initialValues.get('final_mapping_list_id')
    ) {
      hasChanges = true
    }

    // Check criteria
    const currentCriteria = this.criteriaFieldTargets.find(
      (radio) => radio.checked
    )
    if (
      currentCriteria &&
      currentCriteria.value !== this.initialValues.get('criteria')
    ) {
      hasChanges = true
    }

    // Check company_template_id
    if (
      this.companyTemplateFieldTarget.value !==
      this.initialValues.get('company_template_id')
    ) {
      hasChanges = true
    }

    // Check locking_date
    if (
      this.lockingDateFieldTarget.value !==
      this.initialValues.get('locking_date')
    ) {
      hasChanges = true
    }

    // Check mapping_mode
    const currentMappingMode = this.mappingModeFieldTargets.find(
      (radio) => radio.checked
    )
    if (
      currentMappingMode &&
      currentMappingMode.value !== this.initialValues.get('mapping_mode')
    ) {
      hasChanges = true
    }

    // Check file input
    if (this.hasOverrideMappingsFieldTarget) {
      const currentFileSelected =
        this.overrideMappingsFieldTarget.files !== null &&
        this.overrideMappingsFieldTarget.files.length > 0
      if (currentFileSelected !== this.initialFileSelected) {
        hasChanges = true
      }
    }

    // Disable process button if there are unsaved changes
    if (hasChanges) {
      this.processButtonTarget.disabled = true
      this.processButtonTarget.title = 'Please save changes before processing'
    } else {
      // Restore original disabled state (could be disabled for other reasons)
      const originalDisabled =
        this.processButtonTarget.dataset.originalDisabled === 'true'
      this.processButtonTarget.disabled = originalDisabled
      this.processButtonTarget.title = ''
    }
  }
}
